/**
 * The demo's digest envelope (`GET /insights/digest`): the written digest of
 * `../generators/digest.ts` and `./digestSections.ts`, the facts it cites, and
 * its versions. The demo's text is generated by code, so every version is a
 * template summary (`writer: 'template'`) and is never labelled as AI-written.
 * The facts are the same KPI and competition figures the digest quotes,
 * formatted by the same functions. One of them is an estimate so the demo's
 * facts drawer shows how one reads (ADR-0009 D9.11) — a demo stand-in, worked
 * out here and saying so in its rule and assumption; a real estimate is only
 * ever made by a named rule in the API's signals engine, never in the browser.
 */

import type { CitedFact, Digest, DigestPeriod, DigestReport, DigestVersion } from '@/api/types'
import { formatCurrency, formatInteger, formatPercent } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import type { DemoDataset } from '../generators/engine'
import { buildKpis, buildTopCompetitions } from '../generators/overview'

/** As the API: three regenerations per tenant per UTC day, daily and weekly together. */
export const DEMO_REGENERATIONS_PER_DAY = 3

/** The facts behind the demo digest: its two headline figures and the top competition's sell-through and projection. */
export function buildDigestFacts(dataset: DemoDataset, filters: GlobalFilters, from: string, to: string): CitedFact[] {
  const money = { currency: dataset.tenant.currency, locale: dataset.tenant.locale }
  const kpis = buildKpis(dataset, filters).data
  const kpi = (key: string) => kpis.find((item) => item.key === key)
  const facts: CitedFact[] = []
  const period = { periodStart: from, periodEnd: to }
  const range = { range: 'custom', from, to }

  const revenue = kpi('revenue')
  if (revenue) {
    facts.push({
      id: 'f1',
      kind: 'order_value_total',
      basis: 'measured',
      name: dataset.tenant.name,
      value: formatCurrency(revenue.value, money),
      ...(revenue.previous !== null && revenue.previous !== undefined
        ? { comparison: formatCurrency(revenue.previous, money) }
        : {}),
      confidence: 'high',
      ...period,
      drillDown: { path: '/analytics/revenue', params: range },
    })
  }
  const orders = kpi('orders')
  if (orders) {
    facts.push({
      id: 'f2',
      kind: 'orders_total',
      basis: 'measured',
      name: dataset.tenant.name,
      value: formatInteger(orders.value),
      confidence: 'high',
      ...period,
      drillDown: { path: '/analytics/revenue', params: range },
    })
  }

  const [top] = buildTopCompetitions(dataset, filters, 1).data
  if (top) {
    const entity = top.entity
    facts.push({
      id: 'f3',
      kind: 'competition_sell_through',
      basis: 'measured',
      name: entity.label,
      value: formatPercent(top.sellThrough, 0),
      confidence: 'high',
      ...period,
      drillDown: { path: top.drillDown.path, entity, params: range },
    })
    facts.push({
      id: 'f4',
      kind: 'competition_projected_sellout',
      basis: 'estimate',
      name: entity.label,
      value: `${formatPercent(Math.min(1, top.sellThrough + 0.08), 0)}–${formatPercent(Math.min(1, top.sellThrough + 0.16), 0)} (estimate)`,
      confidence: 'low',
      ...period,
      method: 'demo.projected_sellout (demo data)',
      inputs: ['f3'],
      assumption: 'Demo figure: a fixed range above today\'s sell-through, standing in for the API\'s projection rule.',
      drillDown: { path: top.drillDown.path, entity, params: range },
    })
  }
  return facts
}

/** The demo's report for one version of a period's digest. */
export function demoDigestReport(
  period: DigestPeriod,
  from: string,
  to: string,
  digest: Digest,
  facts: CitedFact[],
  versions: readonly DigestVersion[],
  version: number | undefined,
): DigestReport {
  const latest = versions.reduce<DigestVersion | undefined>((best, item) => (!best || item.version > best.version ? item : best), undefined)
  const chosen = version === undefined ? latest : versions.find((item) => item.version === version)
  return {
    period,
    periodStart: from,
    periodEnd: to,
    version: chosen?.version ?? null,
    writer: chosen?.writer ?? null,
    generatedAt: chosen?.generatedAt ?? null,
    digest: chosen ? { ...digest, generatedAt: chosen.generatedAt } : null,
    facts: chosen ? facts : [],
    versions: [...versions].sort((a, b) => b.version - a.version),
    notAvailable: chosen ? null : 'no_digest_yet',
  }
}
